<script setup>
import { onMounted, ref, computed } from 'vue';
import AdminHeader from '../AdminHeader.vue';
import LLMTable from '@/components/table/Table.vue';
import EvalLLMFormModal from '@/components/modals/EvalLLMFormModal.vue';
import { getEvalLLMs, toggleEvalLLMActive } from '@/services/evalLLMService';
import { useToast } from '@/composables/use-toast';

const { addToast } = useToast();

const isLoading = ref(false);
const evalLLMs = ref([]);

// Modal
const isModalOpen = ref(false);
const selectedLLMId = ref(null);

// Search
const searchQuery = ref('');

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(10);

const columns = ref([
    {
        label: 'Hostname',
        key: 'hostname',
        type: 'text',
        sortable: true,
        truncate: 45,
    },
    {
        label: 'Name',
        key: 'name',
        type: 'text',
        sortable: true,
        truncate: 45,
    },
    {
        label: 'Status',
        key: 'active',
        sortable: true,
    },
    {
        label: 'Model Config',
        key: 'model_config',
        sortable: false,
    },
    {
        label: 'Created At',
        key: 'created_at',
        type: 'date',
        sortable: true,
    },
    {
        label: 'Updated At',
        key: 'updated_at',
        type: 'date',
        sortable: true,
    },
    {
        label: 'Actions',
        key: 'actions',
    },
]);

const sortBy = ref('updated_at');
const sortOrder = ref('desc');

const handleSort = (column) => {
    if (sortBy.value === column) {
        sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column;
        sortOrder.value = 'asc';
    }
}

const filteredAndSortedLLMs = computed(() => {
    let result = [...evalLLMs.value];
    
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(llm => {
            const nameMatch = llm.name?.toLowerCase().includes(query);
            const hostnameMatch = llm.hostname?.toLowerCase().includes(query);
            
            let modelConfigMatch = false;
            if (llm.model_config) {
                try {
                    const configStr = typeof llm.model_config === 'string' 
                        ? llm.model_config 
                        : JSON.stringify(llm.model_config);
                    modelConfigMatch = configStr.toLowerCase().includes(query);
                } catch (e) {
                    // If parsing fails, just check the string
                    modelConfigMatch = String(llm.model_config).toLowerCase().includes(query);
                }
            }
            
            let createdAtMatch = false;
            let updatedAtMatch = false;
            
            if (llm.created_at) {
                const createdDate = new Date(llm.created_at);
                const createdDateStr = createdDate.toLocaleString('en-US').toLowerCase();
                const createdDateIso = llm.created_at.toLowerCase();
                createdAtMatch = createdDateStr.includes(query) || createdDateIso.includes(query);
            }
            
            if (llm.updated_at) {
                const updatedDate = new Date(llm.updated_at);
                const updatedDateStr = updatedDate.toLocaleString('en-US').toLowerCase();
                const updatedDateIso = llm.updated_at.toLowerCase();
                updatedAtMatch = updatedDateStr.includes(query) || updatedDateIso.includes(query);
            }
            
            return nameMatch || hostnameMatch || modelConfigMatch || createdAtMatch || updatedAtMatch;
        });
    }
    
    if (sortBy.value) {
        result.sort((a, b) => {
            let aValue = a[sortBy.value];
            let bValue = b[sortBy.value];
            
            const isDateField = ['created_at', 'updated_at'].includes(sortBy.value);
            if (isDateField) {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }
            
            if (sortBy.value === 'active') {
                aValue = Number(aValue);
                bValue = Number(bValue);
            }
            
            const multiplier = sortOrder.value === 'asc' ? 1 : -1;
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return aValue.localeCompare(bValue, undefined, { sensitivity: 'base' }) * multiplier;
            }
            
            if (aValue < bValue) return -1 * multiplier;
            if (aValue > bValue) return 1 * multiplier;
            return 0;
        });
    }
    
    return result;
});

const loadEvalLLMsList = async () => {
    isLoading.value = true;
    
    try {
        const response = await getEvalLLMs();
        evalLLMs.value = response.data.llms;
    } catch (error) {
        addToast(error.response?.data?.message, 'error');
    } finally {
        isLoading.value = false;
    }
};

const handleCreate = () => {
    selectedLLMId.value = null;
    isModalOpen.value = true;
};

const handleEdit = (llm) => {
    selectedLLMId.value = llm.id;
    isModalOpen.value = true;
};

const handleModalClose = () => {
    isModalOpen.value = false;
    selectedLLMId.value = null;
};

const handleModalSaved = () => {
    loadEvalLLMsList();
};

const handleModalDeleted = () => {
    loadEvalLLMsList();
};

const handleToggleActive = async (llm) => {
    try {
        const response = await toggleEvalLLMActive(llm.id, llm.active);
        if (response.status === 200) {
            // Update locally
            llm.active = !llm.active;
            addToast(response.data.message, 'success');
        } else {
            throw new Error(response.data.message);
        }
    } catch (error) {
        addToast(error.message, 'error');
    }
};

const formatModelConfigPreview = (modelConfig) => {
    if (!modelConfig) return '';
    
    try {
        const parsed = typeof modelConfig === 'string' ? JSON.parse(modelConfig) : modelConfig;
        const keys = Object.keys(parsed);
        if (keys.length === 0) return '';
        
        const preview = keys.slice(0, 3).map(key => `${key}: ${typeof parsed[key] === 'object' ? '{...}' : parsed[key]}`).join(', ');
        return keys.length > 3 ? `${preview}, ...` : preview;
    } catch (e) {
        return modelConfig.substring(0, 50) + (modelConfig.length > 50 ? '...' : '');
    }
};

onMounted(() => {
    loadEvalLLMsList();
});
</script>

<template>
    <article class="p-5">
        <header class="bg-echoes-background-black">
            <AdminHeader>
                <template #title>
                    Eval LLMs
                </template>
                <template #description>
                    Manage and organize eval LLMs
                </template>
                <template #actions>
                    <button 
                        type="button"
                        @click="handleCreate" 
                        class="inline-flex items-center justify-center px-4 py-2.5 text-base font-medium rounded-xl bg-echoes-primary text-white hover:bg-echoes-primary/80 transition-colors duration-200"
                    >
                        <i class="mdi mdi-plus mr-2" aria-hidden="true"></i>
                        Create Eval LLM
                    </button>
                </template>
            </AdminHeader>
        </header>

        <main class="bg-echoes-background-black mb-8">
            <!-- Loading State -->
            <div v-if="isLoading" class="flex justify-center items-center py-8">
                <span class="sr-only">Loading Eval LLMs...</span>
                <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-echoes-primary"></div>
            </div>
            <!-- Data Table -->
            <section v-else aria-label="Eval LLMs List">
                <LLMTable 
                :data="filteredAndSortedLLMs" 
                :columns="columns" 
                :empty-message="'No Eval LLMs found'" 
                :total-items="filteredAndSortedLLMs.length"
                :sort-by="sortBy"
                :sort-order="sortOrder"
                @sort="handleSort"
                :search-query="searchQuery"
                @update:search-query="searchQuery = $event"
                :current-page="currentPage"
                @update:current-page="currentPage = $event"
                :items-per-page="itemsPerPage"
                @update:items-per-page="itemsPerPage = $event"
                >
                    <!-- Hostname Column with Icon -->
                    <template #column-hostname="{ item }">
                        <div class="flex items-center gap-3">
                            <!-- Icon Circle -->
                            <div 
                                v-if="item.icon" 
                                class="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden bg-echoes-background-black border border-echoes-border-light flex-shrink-0"
                            >
                                <img 
                                    :src="item.icon" 
                                    :alt="item.name || 'LLM Icon'" 
                                    class="w-full h-full object-cover"
                                />
                            </div>
                            <div 
                                v-else 
                                class="w-8 h-8 rounded-full flex items-center justify-center bg-echoes-background-black border border-echoes-border-light flex-shrink-0"
                            >
                                <i class="mdi mdi-robot text-white/40 text-sm"></i>
                            </div>
                            <!-- Hostname Text -->
                            <span class="text-sm text-white truncate" :title="item.hostname">
                                {{ item.hostname }}
                            </span>
                        </div>
                    </template>

                    <!-- Status Column -->
                    <template #column-active="{ item }">
                        <div class="flex items-center gap-3">
                            <!-- Toggle Switch -->
                            <button
                                type="button"
                                @click="handleToggleActive(item)"
                                :class="[
                                    'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
                                    item.active ? 'bg-green-600' : 'bg-gray-600'
                                ]"
                                :title="item.active ? 'Click to deactivate' : 'Click to activate'"
                                :aria-label="`Toggle LLM status. Currently ${item.active ? 'active' : 'inactive'}`"
                            >
                                <span
                                    aria-hidden="true"
                                    :class="[
                                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                                        item.active ? 'translate-x-5' : 'translate-x-0'
                                    ]"
                                ></span>
                            </button>
                            
                            <!-- Status Label -->
                            <span 
                                :class="[
                                    'text-xs font-medium',
                                    item.active ? 'text-green-400' : 'text-gray-400'
                                ]"
                            >
                                {{ item.active ? 'Active' : 'Inactive' }}
                            </span>
                        </div>
                    </template>

                    <!-- Model Config Column -->
                    <template #column-model_config="{ item }">
                        <div class="flex items-center gap-2">
                            <span 
                                v-if="item.model_config" 
                                class="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-green-600/20 text-green-400 border border-green-600/30"
                                :title="formatModelConfigPreview(item.model_config)"
                            >
                                <i class="mdi mdi-code-json mr-1 text-xs"></i>
                                Configured
                            </span>
                            <span 
                                v-else 
                                class="inline-flex items-center px-2 py-1 text-xs font-medium rounded bg-gray-600/20 text-gray-400 border border-gray-600/30"
                            >
                                Not Set
                            </span>
                        </div>
                    </template>

                    <!-- Actions Column -->
                    <template #column-actions="{ item }">
                        <button
                            type="button"
                            @click="handleEdit(item)"
                           class="inline-flex items-center px-3 py-2 text-sm font-medium rounded-xl bg-echoes-background-black/60 text-white hover:bg-white/5 transition-colors duration-200 border border-echoes-border-light"
                            title="Edit LLM"
                        >
                            <i class="mdi mdi-pencil mr-1"></i>
                            Edit
                        </button>
                    </template>
                </LLMTable>
            </section>
        </main>

        <!-- Modal -->
        <EvalLLMFormModal
            :is-open="isModalOpen"
            :llm-id="selectedLLMId"
            @close="handleModalClose"
            @saved="handleModalSaved"
            @delete="handleModalDeleted"
        />
    </article>
</template>